import Ishikawa from 'components/Ishikawa'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Message, MessageTypeInitiator, RootReducerState } from 'types/global'
import { NotificationAction, NotificationReducer, NotificationType } from 'types/notification'
import { DAMAGE_LEVELS } from "utils/damage"
import NotificationActions from 'actions/Notification'
import { Dispatch } from 'redux'
import { EditButton, TitleIshikawaDiagram, checkInput } from 'pages/NotificationTreatment/NotificationsTreatmentsSteps/IshikawaDiagram/styles'
import { ROUTES_NAMES } from 'constants/name.routes'
import ActionStatus from 'components/ActionStatus'
import { useLocation } from 'react-router-dom'
import NotificationService from 'services/Notification'
import { MdOutlineModeEditOutline } from 'react-icons/md'
import { listOfRoles } from 'components/WithRoleAuthorization/WithRoleAuthorization'
import { UserDataType } from 'types/autentication'
import { notificationStatus } from 'constants/notificationStatus'
import { toBoolean } from 'helper/string'
import { isGraveObitoOrHasImageRisk } from 'utils/notifyEvents'


/**
 * Passo de tratamento 2 fase - Diagrama de Ishikawa
 * @returns
 */

export const canEditIshikawaPerfil = (
    { loggedInUserIsResponsible, isSameLocation, isSameSector, notificationStatusState }:
        { loggedInUserIsResponsible: boolean, isSameLocation: boolean, isSameSector: boolean, notificationStatusState: string }) => {
    if (notificationStatusState === notificationStatus[3].label) {
        return [
            listOfRoles.admin,
            listOfRoles.location_qa,
            listOfRoles.corporate_quality_board
        ]
    } else {
        return [
            ...(loggedInUserIsResponsible ? [listOfRoles.admin] : []),
            ...(isSameLocation && isSameSector && loggedInUserIsResponsible ? [listOfRoles.location_manager] : []),
            ...(isSameLocation && loggedInUserIsResponsible ? [listOfRoles.location_qa] : []),
        ]
    }
}

export interface IshikawaDiagramProps {
    notificationById: NotificationType
    statusIsPendingFinishOrFinished: boolean
    currentUser: UserDataType,
    notificationStatusState: string
}
const IshikawaDiagram = ({ statusIsPendingFinishOrFinished: statusIsArchivedOrFinished, currentUser, notificationById, notificationStatusState }: IshikawaDiagramProps) => {
    const { id } = useParams()
    const navigate = useNavigate()
    const location = useLocation()

    const notificationDispatch: Dispatch<NotificationAction> = useDispatch()


    const notificationSelector: NotificationReducer = useSelector((state: RootReducerState) => state.notificationsReducer)
    const ishikawaDiagramUseMemo = useMemo(() => {
        const currentIshikawa = {
            ...notificationSelector?.ishikawaDiagram,
            ...({ problem: notificationById?.u_reclassified_incident_type?.display_value })
        }
        return currentIshikawa;
    }, [notificationSelector?.ishikawaDiagram])


    const [isDisabled, setIsDisabled] = useState(false)
    const [notAllowed, setNotAllowed] = useState(false)
    const [message, setMessage] = useState<Message>(MessageTypeInitiator)
    let setMessageSuccess = (text: string) => setMessage({ visible: false, status: 1, text: text } as Message)

    const isSameLocation = useMemo(() => {
        return currentUser?.locations && currentUser?.locations?.filter((i) => i.sys_id === notificationById?.location_incident?.value).length > 0
    }, [currentUser, notificationById])
    const isSameSector = useMemo(() => {
        return currentUser?.locations && currentUser?.locations?.filter((i) => i.sys_id === notificationById?.location_incident?.value)[0]?.sectors.filter((i) => i.sys_id === notificationById?.incident_sector?.value).length > 0
    }, [currentUser, notificationById])
    const loggedInUserIsResponsible = useMemo(() => {
        return currentUser?.sys_id && currentUser?.sys_id ===
            notificationById?.u_assigned_to?.value
    }, [currentUser, notificationById])

    /**
     * Carregando o diagrama relacionado ao incidente se existir
     */
    useEffect(() => {
        NotificationActions.getIshikawaByNotificationId(notificationDispatch, id)
    }, [currentUser])

    useEffect(() => {
        let doNotPerformOptionalIsChecked = toBoolean(ishikawaDiagramUseMemo?.do_not_perform_optional)
        if (doNotPerformOptionalIsChecked) {
            ishikawaDiagramUseMemo?.categories.forEach(e => e.causes = [])
        }
        setIsDisabled(doNotPerformOptionalIsChecked)
    }, [ishikawaDiagramUseMemo])

    useEffect(() => {
        if (canEditIshikawaPerfil({ loggedInUserIsResponsible, isSameLocation, isSameSector, notificationStatusState }).includes(currentUser?.profile) && !statusIsArchivedOrFinished) {
            setNotAllowed(false)
        } else {
            setNotAllowed(true)
        }
    }, [statusIsArchivedOrFinished, loggedInUserIsResponsible, isSameLocation, isSameSector, ishikawaDiagramUseMemo])
    useEffect(() => {
        if (location && location.state && location.state.messageSuccess) {
            setMessageSuccess(location.state.messageSuccess)
            setTimeout(() => {
                setMessage({ visible: false })
            }, 3000)

        }
    }, [location])

    /**
     * Redireciona para o form de edição ou criação
     */
    const formIshikawa = () => {
        navigate(`${ROUTES_NAMES.notifications}/analise_ishikawa/${id}`)

    }
    const onChangeCheckedNotPerform = (checkedValue: boolean) => {
        if (checkedValue) {
            ishikawaDiagramUseMemo?.categories?.forEach(e => e.causes = [])
        }
        const ishikawaDiagramWithoutCauses = {
            ...(ishikawaDiagramUseMemo.sys_id ? { sys_id: ishikawaDiagramUseMemo?.sys_id } : {}),
            hospital_incident: id,
            do_not_perform_optional: checkedValue,
            method: "",
            manpower: "",
            material: "",
            measurement: "",
            environment: "",
            machine: "",
            description: notificationById?.u_reclassified_incident_type?.display_value || ' ',
        }

        NotificationService.saveIshikawaDiagram(ishikawaDiagramWithoutCauses).then((response: any) => {
            setIsDisabled(checkedValue)
            NotificationActions.getIshikawaByNotificationId(notificationDispatch, id)
        }).catch((error) => {
            console.log(error)
        })
    }

    const hideOrShowCheckUnrealizedOptional = useMemo(() => {
        const isOlder = notificationById?.u_probability_occurence?.value === ''
        const imageRiskValue = notificationById?.u_image_risk?.value;
        const damageValue = notificationById?.damage_level?.value;

        const checkboxNotRequired = isGraveObitoOrHasImageRisk({ damageLevelValue: damageValue, imageRiskValue: imageRiskValue }) || notificationById?.u_never_events_or_sentinel_event?.value == 'yes'
        if (checkboxNotRequired) {
            return true
        }

        if (isOlder) {
            const ishikawaRequiredDamages = DAMAGE_LEVELS.filter(({ value }) => value === 'sem-dano' || value === 'leve' || value === 'moderado')
            const ishikawaRequired = ishikawaRequiredDamages?.find(({ id }) => id === damageValue)
            return !ishikawaRequired
        } else {
            const ishikawaMandatory = notificationSelector?.notificationProcessRequiredStepsRules?.ishikawaMandatory
            return ishikawaMandatory === 'Optional';
        }

    }, [currentUser])


    return (
        <div data-testid="test-notification-treatment-step2">
            <TitleIshikawaDiagram>
                Análise do Diagrama de Ishikawa
            </TitleIshikawaDiagram>
            <div className="px-8 flex flex-row gap-12 mt-6 mb-1">
                Ao preencher este diagrama, visualizamos a causa real do
                Incidente ou Evento adverso. O seu objetivo é identificar e
                classificar os principais fatores que levaram o problema (efeito
                inesperado) acontecer.
            </div>
            <div className="px-8 flex flex-row gap-12 mt-1 mb-10 text-[14px] text-gray-500">
                Preenchimento obrigatório para todas notificações, exceto as
                graves e óbitos (facultativas).
            </div>
            <fieldset disabled={notAllowed}>
                <div className="px-8 flex flex-row gap-12 mt-6 mb-10">
                    <EditButton type="button" disabled={isDisabled || notAllowed} onClick={formIshikawa}
                        className={`${isDisabled || notAllowed ? 'bg-disabled text-textSelect font-normal' : ''}`}>
                        <div className="w-[202px] flex justify-center items-center gap-3">
                            <MdOutlineModeEditOutline size={18} color={`${!isDisabled || notAllowed ? 'white' : '#4F4F4F'}`} />
                            <span className="inline-block align-middle">{!ishikawaDiagramUseMemo?.sys_id ? 'Preencher diagrama' : 'Editar diagrama'}</span>
                        </div>
                    </EditButton>

                    {hideOrShowCheckUnrealizedOptional &&
                        <div className="mt-2 block min-h-[1.5rem] pl-[1.5rem]">
                            <input
                                onChange={(e) => {
                                    const isChecked = e.target.checked
                                    onChangeCheckedNotPerform(isChecked)
                                }}
                                checked={isDisabled}
                                name='do_not_perform_optional'
                                className={`${checkInput}  ${notAllowed ? "hover:cursor-default" : "hover:cursor-pointer"}`}
                                type="checkbox"
                                id="checkUnrealizedOptional"
                            />
                            <label
                                className={`inline-block pl-[0.15rem] ${notAllowed ? "opacity-40" : "hover:cursor-pointer"}`}
                                htmlFor="checkUnrealizedOptional"
                            >
                                Não realizar o preenchimento
                            </label>
                        </div>
                    }
                </div>

                {message.visible && (
                    <div className="px-8 m-0">
                        <ActionStatus
                            text={message.text}
                            status={message.status}
                        />
                    </div>
                )}

                <div className="px-8 m-0">
                    <Ishikawa data={ishikawaDiagramUseMemo} />
                </div>
            </fieldset>
        </div>
    )
}

export { IshikawaDiagram }


